/**
 * `<DependencyEdgePopover/>` — the edge popover for a selected dependency
 * (B2.7 + #929 frontend).
 *
 * When a lead clicks a dependency arrow on the canvas it selects that edge
 * (`treeStore.selectedEdgeId`); this small card floats near the edge's
 * midpoint and lets them inspect + act on the link. Three actions, all about
 * the WORK relationship, never a person (§A9 brand firewall):
 *
 *   1. The relationship summary — factual: "'{from}' comes before '{to}'"
 *      (the gallery B2.7 wording). Calm, no judgement.
 *   2. An editable NOTE — the free-text label for "what kind of link this is"
 *      (e.g. "needs sign-off first"). Save persists it; Clear removes it. The
 *      current note pre-fills the field. PATCH, max 500 chars.
 *   3. Insert a task BETWEEN — an inline title input that splits the edge into
 *      `from ──▶ new ──▶ to` atomically (the new node is a sibling of `to`).
 *   4. Remove — drops the edge, behind a one-click calm confirm.
 *
 * Presentational + self-contained: it holds only its own transient UI (the
 * note draft, the confirm-arm, the insert draft) and fires the parent's
 * handlers. The parent owns the mutations + query invalidation; this never
 * touches the network. It lives INSIDE the canvas plane (so it tracks
 * pan/zoom with the edge); the parent positions it.
 *
 * Editorial revamp (#1043, Wave 1 · B2.7): theme-aware. The style forks on the
 * per-team `task_editorial_ui` flag via `useMapTheme()`:
 *   - LEGACY (flag off): byte-identical to today — the white card, teal "Save
 *     note", terracotta "Insert", `#B45309` remove affordance.
 *   - EDITORIAL (flag on): the wireframe `V_B27` linking surface — a `.t-banner`
 *     `--teal-soft` info wash on a teal hairline, a mono `LINKING MODE` kicker
 *     above the factual "comes before" relationship summary, editorial inputs,
 *     and ink `.t-btn`-style confirm controls with teal accents.
 * The relationship copy, the four actions (note / insert / remove / close),
 * behaviour, and every `data-testid` are unchanged.
 */

import { useEffect, useRef, useState } from "react";

import { useMapTheme } from "../theme/MapThemeContext";
import { PALETTE } from "../tree/palette";
import { DEPENDENCY_ARROW_COLOR } from "./DependencyArrows";

/** The max note length the backend enforces (mirrored here for the counter). */
const NOTE_MAX = 500;

export interface DependencyEdgePopoverProps {
  /** The selected edge's id. */
  dependencyId: string;
  /** The earlier task's title (`from` — comes before). */
  fromTitle: string;
  /** The later task's title (`to` — the arrow points here). */
  toTitle: string;
  /** The edge's current free-text note, or null/undefined when unset. */
  note?: string | null | undefined;
  /** True while a note save / insert / remove mutation is in flight. */
  pending?: boolean | undefined;
  /** Persist (or clear, when empty) the edge's note. */
  onSetNote: (note: string | null) => void;
  /** Insert a new task between the endpoints (splits the edge). */
  onInsertBetween: (title: string) => void;
  /** Remove the edge. */
  onRemove: () => void;
  /** Close the popover (clears the edge selection). */
  onClose: () => void;
}

export function DependencyEdgePopover({
  dependencyId,
  fromTitle,
  toTitle,
  note,
  pending,
  onSetNote,
  onInsertBetween,
  onRemove,
  onClose,
}: DependencyEdgePopoverProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  // Editorial accents: the dependency teal (matches the arrows + connector
  // port, so the popover reads as one gesture with the edge). The "Insert"
  // affordance keeps the legacy terracotta-ish dependency hue off, teal on.
  const insertAccent = editorial ? c.teal : DEPENDENCY_ARROW_COLOR;
  // Calm, never-alarm remove affordance: editorial uses the calm amber state
  // ink (`--st-block` family), legacy keeps the shipped `#B45309`.
  const removeAccent = editorial ? theme.stateStyle("blocked").fg : "#B45309";

  // The note draft is seeded from the edge's current note and re-seeded
  // whenever the SELECTED edge changes (a different edge → its own note).
  const [noteDraft, setNoteDraft] = useState(note ?? "");
  const [insertDraft, setInsertDraft] = useState("");
  // Two-step calm confirm for Remove: first click arms, second confirms.
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-seed on edge change
  useEffect(() => {
    setNoteDraft(note ?? "");
    setInsertDraft("");
    setConfirmingRemove(false);
  }, [dependencyId]);

  const insertRef = useRef<HTMLInputElement>(null);

  const noteChanged = (noteDraft.trim() || null) !== (note?.trim() || null);
  const insertTitle = insertDraft.trim();

  const submitInsert = () => {
    if (insertTitle.length === 0) return;
    onInsertBetween(insertTitle);
  };

  return (
    <div
      data-testid="dependency-edge-popover"
      // Stop the pointer-down bubbling to the canvas (which starts a pan / clears
      // the selection on background pointer-down) so interacting inside the
      // popover never disturbs the canvas. Pointer-down is the only canvas
      // gesture to contain; no onClick handler is needed (so no a11y key pairing).
      onPointerDown={(e) => e.stopPropagation()}
      className={
        editorial ? "w-72 rounded-lg border p-3" : "w-72 rounded-lg border bg-white p-3 shadow-lg"
      }
      style={
        editorial
          ? {
              // The `.t-banner.info` teal-soft wash on a teal hairline.
              borderColor: "rgba(63,139,139,0.3)",
              backgroundColor: c.tealSoft,
              color: c.ink,
              borderRadius: theme.radii.lg,
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              fontFamily: theme.fonts.sans,
            }
          : { borderColor: PALETTE.softBorder, color: PALETTE.ink }
      }
    >
      {/* Header: the factual relationship summary + close. Editorial leads with
          a mono `LINKING MODE` kicker (the wireframe `.t-issue`). */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {editorial && (
            <div
              className="mb-1 text-[10px] font-medium uppercase"
              style={{ fontFamily: theme.fonts.mono, letterSpacing: "0.08em", color: c.tealInk }}
            >
              Linking mode
            </div>
          )}
          <p data-testid="dependency-edge-summary" className="text-xs leading-snug">
            <span className="font-semibold">“{fromTitle}”</span>
            <span style={{ color: editorial ? c.muted : PALETTE.muted }}> comes before </span>
            <span className="font-semibold">“{toTitle}”</span>
          </p>
        </div>
        <button
          type="button"
          data-testid="dependency-edge-close"
          aria-label="Close"
          onClick={onClose}
          className="-mr-1 -mt-1 rounded p-1 text-sm leading-none hover:bg-slate-50"
          style={{ color: editorial ? c.muted : PALETTE.faint }}
        >
          ✕
        </button>
      </div>

      {/* The free-text note — what KIND of link this is. */}
      <div className="mt-3">
        <label
          htmlFor={`dep-note-${dependencyId}`}
          className="block text-[11px] font-semibold"
          style={{ color: editorial ? c.slate : PALETTE.slate }}
        >
          What kind of link
        </label>
        <textarea
          id={`dep-note-${dependencyId}`}
          data-testid="dependency-edge-note-input"
          value={noteDraft}
          maxLength={NOTE_MAX}
          rows={2}
          placeholder="e.g. needs sign-off first"
          onChange={(e) => setNoteDraft(e.target.value)}
          className="mt-1 w-full resize-none rounded-md border px-2 py-1.5 text-xs focus:outline-none focus:ring-1"
          style={
            editorial
              ? { borderColor: c.rule2, backgroundColor: c.cardBg, color: c.ink }
              : { borderColor: PALETTE.softBorder }
          }
        />
        <div className="mt-1.5 flex items-center gap-2">
          <button
            type="button"
            data-testid="dependency-edge-note-save"
            disabled={pending || !noteChanged}
            onClick={() => onSetNote(noteDraft.trim() || null)}
            className={
              editorial
                ? "rounded-md border px-2.5 py-1 text-[11px] font-medium transition-opacity hover:opacity-90 disabled:opacity-40"
                : "rounded-md px-2.5 py-1 text-[11px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
            }
            style={
              editorial
                ? { borderColor: c.ink, backgroundColor: c.ink, color: c.canvas }
                : { backgroundColor: PALETTE.teal }
            }
          >
            Save note
          </button>
          {(note?.trim() ?? "").length > 0 && (
            <button
              type="button"
              data-testid="dependency-edge-note-clear"
              disabled={pending}
              onClick={() => {
                setNoteDraft("");
                onSetNote(null);
              }}
              className="rounded-md px-2 py-1 text-[11px] font-medium transition-colors hover:bg-slate-50 disabled:opacity-40"
              style={{ color: editorial ? c.muted : PALETTE.muted }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Insert a task between the two endpoints (splits the edge). */}
      <div
        className="mt-3 border-t pt-3"
        style={{ borderColor: editorial ? "rgba(63,139,139,0.25)" : PALETTE.softBorder }}
      >
        <label
          htmlFor={`dep-insert-${dependencyId}`}
          className="block text-[11px] font-semibold"
          style={{ color: editorial ? c.slate : PALETTE.slate }}
        >
          Insert a task between
        </label>
        <div className="mt-1 flex items-center gap-1.5">
          <input
            ref={insertRef}
            id={`dep-insert-${dependencyId}`}
            data-testid="dependency-edge-insert-input"
            value={insertDraft}
            placeholder="New task title"
            onChange={(e) => setInsertDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitInsert();
              }
            }}
            className="min-w-0 flex-1 rounded-md border px-2 py-1.5 text-xs focus:outline-none focus:ring-1"
            style={
              editorial
                ? { borderColor: c.rule2, backgroundColor: c.cardBg, color: c.ink }
                : { borderColor: PALETTE.softBorder }
            }
          />
          <button
            type="button"
            data-testid="dependency-edge-insert-submit"
            disabled={pending || insertTitle.length === 0}
            onClick={submitInsert}
            className="shrink-0 rounded-md border px-2 py-1.5 text-[11px] font-semibold transition-colors hover:bg-slate-50 disabled:opacity-40"
            style={{ borderColor: insertAccent, color: insertAccent }}
          >
            Insert
          </button>
        </div>
      </div>

      {/* Remove the link — calm two-step confirm. */}
      <div
        className="mt-3 border-t pt-3"
        style={{ borderColor: editorial ? "rgba(63,139,139,0.25)" : PALETTE.softBorder }}
      >
        {confirmingRemove ? (
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px]" style={{ color: editorial ? c.muted : PALETTE.muted }}>
              Remove this link?
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                data-testid="dependency-edge-remove-cancel"
                onClick={() => setConfirmingRemove(false)}
                className="rounded-md px-2 py-1 text-[11px] font-medium transition-colors hover:bg-slate-50"
                style={{ color: editorial ? c.muted : PALETTE.muted }}
              >
                Keep
              </button>
              <button
                type="button"
                data-testid="dependency-edge-remove-confirm"
                disabled={pending}
                onClick={onRemove}
                className={
                  editorial
                    ? "rounded-md border px-2.5 py-1 text-[11px] font-semibold transition-opacity hover:opacity-90 disabled:opacity-40"
                    : "rounded-md px-2.5 py-1 text-[11px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
                }
                style={
                  editorial
                    ? {
                        borderColor: removeAccent,
                        color: removeAccent,
                        backgroundColor: "transparent",
                      }
                    : { backgroundColor: "#B45309" }
                }
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            data-testid="dependency-edge-remove"
            onClick={() => setConfirmingRemove(true)}
            className="text-[11px] font-medium transition-colors hover:underline"
            style={{ color: removeAccent }}
          >
            Remove this link
          </button>
        )}
      </div>
    </div>
  );
}
